"use client"

import { useState, useEffect } from "react"
import { getDatabase, ref, onValue } from "firebase/database"
import { FaDownload } from "react-icons/fa"

function parseCustomDate(dateString) {
  if (!dateString || typeof dateString !== "string") return null

  const timestamp = Number(dateString)
  if (!isNaN(timestamp) && timestamp > 0) {
    return new Date(timestamp)
  }

  const match = dateString.match(/(\d{1,2})\/(\d{1,2})\/(\d{4})\s+(\d{1,2}):(\d{2})\s*(am|pm)/i)
  if (match) {
    const [, day, month, year, hour, minute, period] = match
    let hours = Number.parseInt(hour)
    if (period.toLowerCase() === "pm" && hours !== 12) hours += 12
    if (period.toLowerCase() === "am" && hours === 12) hours = 0

    return new Date(
      Number.parseInt(year),
      Number.parseInt(month) - 1,
      Number.parseInt(day),
      hours,
      Number.parseInt(minute),
    )
  }

  const date = new Date(dateString)
  return isNaN(date.getTime()) ? null : date
}

export default function SupplierFinance() {
  const [financeData, setFinanceData] = useState([])
  const [loanRequests, setLoanRequests] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState("")
  const [searchTerm, setSearchTerm] = useState("")
  const [viewingLoan, setViewingLoan] = useState(null)
  const [activeTab, setActiveTab] = useState("applications")
  const [sortConfig, setSortConfig] = useState({
    key: "timestamp",
    direction: "desc",
  })

  useEffect(() => {
    const fetchData = async () => {
      try {
        const db = getDatabase()

        const financeRef = ref(db, "Finance")
        onValue(
          financeRef,
          (snapshot) => {
            if (snapshot.exists()) {
              const data = snapshot.val()
              const financeList = []

              Object.keys(data).forEach((userId) => {
                if (typeof data[userId] === "object") {
                  Object.keys(data[userId]).forEach((applicationId) => {
                    const applicationData = data[userId][applicationId]
                    if (applicationData && typeof applicationData === "object") {
                      const parsedDate = parseCustomDate(applicationData.timestamp)
                      if (parsedDate) {
                        financeList.push({
                          userId,
                          applicationId,
                          timestamp: applicationData.timestamp,
                          ...applicationData,
                        })
                      }
                    }
                  })
                }
              })

              financeList.sort((a, b) => {
                const dateA = parseCustomDate(a.timestamp)
                const dateB = parseCustomDate(b.timestamp)
                return (dateB?.getTime() || 0) - (dateA?.getTime() || 0)
              })

              setFinanceData(financeList)
            } else {
              setFinanceData([])
            }
            setIsLoading(false)
          },
          (error) => {
            console.error("Error fetching finance data:", error)
            setError("Error fetching finance data: " + error.message)
            setIsLoading(false)
          },
        )

        const loanRequestRef = ref(db, "loanRequest")
        onValue(
          loanRequestRef,
          (snapshot) => {
            if (snapshot.exists()) {
              const data = snapshot.val()
              const requestList = []

              Object.keys(data).forEach((userId) => {
                if (typeof data[userId] === "object") {
                  Object.keys(data[userId]).forEach((requestId) => {
                    const requestData = data[userId][requestId]
                    if (requestData && typeof requestData === "object") {
                      requestList.push({
                        userId,
                        requestId,
                        ...requestData,
                      })
                    }
                  })
                }
              })

              requestList.sort((a, b) => b.timestamp - a.timestamp)
              setLoanRequests(requestList)
            } else {
              setLoanRequests([])
            }
          },
          (error) => {
            console.error("Error fetching loan requests:", error)
          },
        )
      } catch (err) {
        console.error("Error in data fetch:", err)
        setError("Error fetching data: " + err.message)
        setIsLoading(false)
      }
    }

    fetchData()
  }, [])

  const handleSort = (key) => {
    let direction = "asc"
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc"
    }
    setSortConfig({ key, direction })
  }

  const getNestedValue = (obj, path) => {
    return path.split(".").reduce((current, key) => current && current[key], obj) || ""
  }

  const sortedData = [...financeData].sort((a, b) => {
    let aValue = getNestedValue(a, sortConfig.key)
    let bValue = getNestedValue(b, sortConfig.key)

    if (sortConfig.key === "timestamp") {
      const dateA = parseCustomDate(aValue)
      const dateB = parseCustomDate(bValue)
      aValue = dateA?.getTime() || 0
      bValue = dateB?.getTime() || 0
    } else {
      if (typeof aValue === "string") aValue = aValue.toLowerCase()
      if (typeof bValue === "string") bValue = bValue.toLowerCase()
    }

    if (aValue < bValue) return sortConfig.direction === "asc" ? -1 : 1
    if (aValue > bValue) return sortConfig.direction === "asc" ? 1 : -1
    return 0
  })

  const filteredData = sortedData.filter((item) => {
    const searchLower = searchTerm.toLowerCase()
    return (
      item.personalInfo?.firstName?.toLowerCase().includes(searchLower) ||
      item.personalInfo?.lastName?.toLowerCase().includes(searchLower) ||
      item.personalInfo?.email?.toLowerCase().includes(searchLower) ||
      item.personalInfo?.company?.toLowerCase().includes(searchLower) ||
      item.financialDetails?.loanType?.toLowerCase().includes(searchLower) ||
      item.financialDetails?.loanAmount?.toString().includes(searchTerm) ||
      item.applicationId?.toLowerCase().includes(searchLower)
    )
  })

  const filteredRequests = loanRequests.filter((item) => {
    const searchLower = searchTerm.toLowerCase()
    return (
      item.fullName?.toLowerCase().includes(searchLower) ||
      item.email?.toLowerCase().includes(searchLower) ||
      item.phone?.includes(searchTerm) ||
      item.message?.toLowerCase().includes(searchLower)
    )
  })

  const exportToCSV = (type) => {
    if (type === "applications") {
      const headers = [
        "Application ID",
        "First Name",
        "Last Name",
        "Company Name",
        "Email",
        "Phone",
        "PAN",
        "GSTIN",
        "Date of Birth",
        "Address",
        "Loan Type",
        "Loan Amount",
        "Tenure",
        "Monthly Income",
        "Employment Type",
        "Company",
        "Documents Status",
        "Status",
        "Timestamp",
      ]

      const csvData = [
        headers.join(","),
        ...filteredData.map((item) => {
          const docStatus = item.documents
            ? Object.entries(item.documents)
              .map(([key, value]) => `${key}:${value}`)
              .join(";")
            : ""

          return [
            item.applicationId || "",
            item.personalInfo?.firstName || "",
            item.personalInfo?.lastName || "",
            item.personalInfo?.company || "",
            item.personalInfo?.email || "",
            item.personalInfo?.phone || "",
            item.personalInfo?.pan || "",
            item.personalInfo?.gstin || "",
            item.personalInfo?.dateOfBirth || "",
            item.personalInfo?.address || "",
            item.financialDetails?.loanType || "",
            item.financialDetails?.loanAmount || "",
            item.financialDetails?.tenure || "",
            item.financialDetails?.monthlyIncome || "",
            item.financialDetails?.employmentType || "",
            item.financialDetails?.company || "",
            docStatus,
            item.status || "",
            item.timestamp || "",
          ]
            .map((field) => `"${String(field).replace(/"/g, '""')}"`)
            .join(",")
        }),
      ].join("\n")

      const blob = new Blob([csvData], { type: "text/csv;charset=utf-8;" })
      const url = URL.createObjectURL(blob)
      const link = document.createElement("a")
      link.setAttribute("href", url)
      link.setAttribute("download", `finance_applications_${new Date().toISOString().split("T")[0]}.csv`)
      link.style.visibility = "hidden"
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    } else {
      const headers = ["Full Name", "Email", "Phone", "Message", "Timestamp"]

      const csvData = [
        headers.join(","),
        ...filteredRequests.map((item) =>
          [
            item.fullName || "",
            item.email || "",
            item.phone || "",
            item.message || "",
            new Date(item.timestamp).toLocaleString(),
          ]
            .map((field) => `"${String(field).replace(/"/g, '""')}"`)
            .join(","),
        ),
      ].join("\n")

      const blob = new Blob([csvData], { type: "text/csv;charset=utf-8;" })
      const url = URL.createObjectURL(blob)
      const link = document.createElement("a")
      link.setAttribute("href", url)
      link.setAttribute("download", `loan_requests_${new Date().toISOString().split("T")[0]}.csv`)
      link.style.visibility = "hidden"
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    }
  }

  const getDocumentStatusIcon = (documents) => {
    if (!documents) return "❌"
    const uploadedCount = Object.values(documents).filter((status) => status === "yes").length
    const totalCount = Object.keys(documents).length
    if (uploadedCount === 0) return "❌"
    if (uploadedCount === totalCount) return "✅"
    return "⚠️"
  }

  if (isLoading) {
    return (
      <div style={{ display: "flex", minHeight: "100vh", alignItems: "center", justifyContent: "center" }}>
        <div style={{ textAlign: "center" }}>
          <div
            style={{
              display: "inline-block",
              width: "32px",
              height: "32px",
              border: "4px solid currentColor",
              borderRightColor: "transparent",
              borderRadius: "50%",
              animation: "spin 1s linear infinite",
            }}
          />
          <p style={{ marginTop: "1rem", color: "#64748b" }}>Loading finance data...</p>
          <style jsx>{`
            @keyframes spin {
              to {
                transform: rotate(360deg);
              }
            }
          `}</style>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div style={{ display: "flex", minHeight: "100vh", alignItems: "center", justifyContent: "center" }}>
        <div style={{ color: "#ef4444" }}>{error}</div>
      </div>
    )
  }

  return (
    <div style={{ maxWidth: "1200px", margin: "0 auto", padding: "1.5rem" }}>
      <div style={{ marginBottom: "1.5rem" }}>
        <h1
          style={{
            fontSize: "1.875rem",
            fontWeight: "bold",
            marginBottom: "0.25rem",
          }}
        >
          Finance Management
        </h1>
        <p style={{ fontSize: "0.875rem", color: "#64748b" }}>
          Manage loan applications and requests from Utkarsh Small Finance Bank
          Limited
        </p>
      </div>

      {/* Tabs */}
      <div style={{ marginBottom: "1.5rem" }}>
        <div
          style={{ borderBottom: "1px solid #e2e8f0", marginBottom: "1rem" }}
        >
          <button
            onClick={() => setActiveTab("applications")}
            style={{
              padding: "0.5rem 1rem",
              marginRight: "0.5rem",
              border: "none",
              background: activeTab === "applications" ? "#fff" : "transparent",
              borderBottom:
                activeTab === "applications"
                  ? "2px solid #3b82f6"
                  : "2px solid transparent",
              color: activeTab === "applications" ? "#3b82f6" : "#64748b",
              cursor: "pointer",
              fontWeight: activeTab === "applications" ? "600" : "400",
            }}
          >
            Applications ({financeData.length})
          </button>
          <button
            onClick={() => setActiveTab("requests")}
            style={{
              padding: "0.5rem 1rem",
              border: "none",
              background: activeTab === "requests" ? "#fff" : "transparent",
              borderBottom:
                activeTab === "requests"
                  ? "2px solid #3b82f6"
                  : "2px solid transparent",
              color: activeTab === "requests" ? "#3b82f6" : "#64748b",
              cursor: "pointer",
              fontWeight: activeTab === "requests" ? "600" : "400",
            }}
          >
            Loan Requests ({loanRequests.length})
          </button>
        </div>
      </div>

      {/* Applications Tab */}
      {activeTab === "applications" && (
        <div>
          <div
            style={{
              display: "flex",
              gap: "1rem",
              marginBottom: "1.5rem",
              flexWrap: "wrap",
            }}
          >
            <div style={{ position: "relative", flex: "1", minWidth: "250px" }}>
              <input
                type="text"
                placeholder="Search applications..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{
                  width: "100%",
                  padding: "0.5rem 0.5rem 0.5rem 2.5rem",
                  border: "1px solid #e2e8f0",
                  borderRadius: "0.375rem",
                  fontSize: "0.875rem",
                }}
              />
              <span
                style={{
                  position: "absolute",
                  left: "0.75rem",
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: "#94a3b8",
                }}
              >
                🔍
              </span>
            </div>

            <button
              onClick={() => exportToCSV("applications")}
              style={{
                padding: "0.5rem 1rem",
                border: "none",
                background: "white",
                color: "#0074cc",
                cursor: "pointer",
                fontSize: "0.875rem",
                display: "flex",
                alignItems: "center",
                gap: "0.25rem",
              }}
            >
              <FaDownload size={14} /> Export
            </button>
          </div>

          {filteredData.length === 0 ? (
            <div
              style={{
                border: "1px solid #e2e8f0",
                borderRadius: "0.5rem",
                padding: "3rem",
                textAlign: "center",
                color: "#64748b",
              }}
            >
              No finance applications found matching your search criteria.
            </div>
          ) : (
            <div
              style={{
                overflowX: "auto",
                border: "1px solid #e2e8f0",
                borderRadius: "0.5rem",
              }}
            >
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead style={{ background: "#f8fafc" }}>
                  <tr>
                    <th
                      style={{
                        padding: "0.75rem",
                        textAlign: "left",
                        fontWeight: "600",
                        fontSize: "0.875rem",
                      }}
                    >
                      #
                    </th>
                    <th
                      onClick={() => handleSort("applicationId")}
                      style={{
                        padding: "0.75rem",
                        textAlign: "left",
                        fontWeight: "600",
                        fontSize: "0.875rem",
                        cursor: "pointer",
                      }}
                    >
                      Application ID
                    </th>
                    <th
                      onClick={() => handleSort("personalInfo.firstName")}
                      style={{
                        padding: "0.75rem",
                        textAlign: "left",
                        fontWeight: "600",
                        fontSize: "0.875rem",
                        cursor: "pointer",
                      }}
                    >
                      Name
                    </th>
                    <th
                      onClick={() => handleSort("personalInfo.company")}
                      style={{
                        padding: "0.75rem",
                        textAlign: "left",
                        fontWeight: "600",
                        fontSize: "0.875rem",
                        cursor: "pointer",
                      }}
                    >
                      Company
                    </th>
                    <th
                      onClick={() => handleSort("financialDetails.loanType")}
                      style={{
                        padding: "0.75rem",
                        textAlign: "left",
                        fontWeight: "600",
                        fontSize: "0.875rem",
                        cursor: "pointer",
                      }}
                    >
                      Loan Type
                    </th>
                    <th
                      onClick={() => handleSort("financialDetails.loanAmount")}
                      style={{
                        padding: "0.75rem",
                        textAlign: "left",
                        fontWeight: "600",
                        fontSize: "0.875rem",
                        cursor: "pointer",
                      }}
                    >
                      Amount
                    </th>
                    <th
                      style={{
                        padding: "0.75rem",
                        textAlign: "left",
                        fontWeight: "600",
                        fontSize: "0.875rem",
                      }}
                    >
                      Phone
                    </th>
                    <th
                      style={{
                        padding: "0.75rem",
                        textAlign: "center",
                        fontWeight: "600",
                        fontSize: "0.875rem",
                      }}
                    >
                      Docs
                    </th>
                    <th
                      onClick={() => handleSort("status")}
                      style={{
                        padding: "0.75rem",
                        textAlign: "left",
                        fontWeight: "600",
                        fontSize: "0.875rem",
                        cursor: "pointer",
                      }}
                    >
                      Status
                    </th>
                    <th
                      onClick={() => handleSort("timestamp")}
                      style={{
                        padding: "0.75rem",
                        textAlign: "left",
                        fontWeight: "600",
                        fontSize: "0.875rem",
                        cursor: "pointer",
                      }}
                    >
                      Timestamp
                    </th>
                    <th
                      style={{
                        padding: "0.75rem",
                        textAlign: "left",
                        fontWeight: "600",
                        fontSize: "0.875rem",
                      }}
                    >
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredData.map((loan, index) => (
                    <tr
                      key={loan.applicationId || index}
                      style={{ borderTop: "1px solid #e2e8f0" }}
                    >
                      <td style={{ padding: "0.75rem", fontSize: "0.875rem" }}>
                        {index + 1}
                      </td>
                      <td
                        style={{
                          padding: "0.75rem",
                          fontSize: "0.875rem",
                          fontFamily: "monospace",
                        }}
                      >
                        {loan.applicationId || "N/A"}
                      </td>
                      <td style={{ padding: "0.75rem", fontSize: "0.875rem" }}>
                        {`${loan.personalInfo?.firstName || ""} ${loan.personalInfo?.lastName || ""
                          }`.trim() || "N/A"}
                      </td>
                      <td style={{ padding: "0.75rem", fontSize: "0.875rem" }}>
                        {loan.personalInfo?.company || "N/A"}
                      </td>
                      <td style={{ padding: "0.75rem", fontSize: "0.875rem" }}>
                        {loan.financialDetails?.loanType || "N/A"}
                      </td>
                      <td style={{ padding: "0.75rem", fontSize: "0.875rem" }}>
                        ₹
                        {loan.financialDetails?.loanAmount?.toLocaleString() ||
                          "0"}
                      </td>
                      <td style={{ padding: "0.75rem", fontSize: "0.875rem" }}>
                        {loan.personalInfo?.phone || "N/A"}
                      </td>
                      <td
                        style={{
                          padding: "0.75rem",
                          fontSize: "0.875rem",
                          textAlign: "center",
                        }}
                      >
                        {getDocumentStatusIcon(loan.documents)}
                      </td>
                      <td style={{ padding: "0.75rem", fontSize: "0.875rem" }}>
                        <span
                          style={{
                            padding: "0.25rem 0.5rem",
                            borderRadius: "0.25rem",
                            background:
                              loan.status === "submitted"
                                ? "#f1f5f9"
                                : "#dbeafe",
                            color:
                              loan.status === "submitted"
                                ? "#475569"
                                : "#1e40af",
                            fontSize: "0.75rem",
                            fontWeight: "500",
                          }}
                        >
                          {loan.status || "Unknown"}
                        </span>
                      </td>
                      <td
                        style={{
                          padding: "0.75rem",
                          fontSize: "0.75rem",
                          color: "#64748b",
                        }}
                      >
                        {parseCustomDate(loan.timestamp)?.toLocaleString() ||
                          "N/A"}
                      </td>
                      <td style={{ padding: "0.75rem" }}>
                        <button
                          onClick={() => setViewingLoan(loan)}
                          style={{
                            padding: "0.25rem 0.5rem",
                            border: "1px solid #e2e8f0",
                            background: "#0074cc",
                            cursor: "pointer",
                            fontSize: "0.875rem",
                            color: "white",
                            borderRadius: "0.25rem",
                          }}
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Loan Requests Tab */}
      {activeTab === "requests" && (
        <div>
          <div
            style={{
              display: "flex",
              gap: "1rem",
              marginBottom: "1.5rem",
              flexWrap: "wrap",
            }}
          >
            <div style={{ position: "relative", flex: "1", minWidth: "250px" }}>
              <input
                type="text"
                placeholder="Search requests..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{
                  width: "100%",
                  padding: "0.5rem 0.5rem 0.5rem 2.5rem",
                  border: "1px solid #e2e8f0",
                  borderRadius: "0.375rem",
                  fontSize: "0.875rem",
                }}
              />
              <span
                style={{
                  position: "absolute",
                  left: "0.75rem",
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: "#94a3b8",
                }}
              >
                🔍
              </span>
            </div>
          </div>

          {filteredRequests.length === 0 ? (
            <div
              style={{
                border: "1px solid #e2e8f0",
                borderRadius: "0.5rem",
                padding: "3rem",
                textAlign: "center",
                color: "#64748b",
              }}
            >
              No loan requests found matching your search criteria.
            </div>
          ) : (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
                gap: "1rem",
              }}
            >
              {filteredRequests.map((request) => (
                <div
                  key={request.requestId}
                  style={{
                    border: "1px solid #e2e8f0",
                    borderRadius: "0.5rem",
                    padding: "1rem",
                    background: "#fff",
                  }}
                >
                  <h3
                    style={{
                      fontSize: "1.125rem",
                      fontWeight: "600",
                      marginBottom: "0.25rem",
                    }}
                  >
                    {request.fullName}
                  </h3>
                  <p
                    style={{
                      fontSize: "0.875rem",
                      color: "#64748b",
                      marginBottom: "1rem",
                    }}
                  >
                    {request.email}
                  </p>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      fontSize: "0.875rem",
                      marginBottom: "0.5rem",
                    }}
                  >
                    <span style={{ color: "#64748b" }}>Phone:</span>
                    <span style={{ fontWeight: "500" }}>{request.phone}</span>
                  </div>
                  <div style={{ fontSize: "0.875rem", marginBottom: "0.5rem" }}>
                    <span
                      style={{
                        color: "#64748b",
                        display: "block",
                        marginBottom: "0.25rem",
                      }}
                    >
                      Message:
                    </span>
                    <p style={{ margin: 0 }}>{request.message}</p>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      fontSize: "0.875rem",
                      paddingTop: "0.75rem",
                      borderTop: "1px solid #e2e8f0",
                      marginTop: "0.75rem",
                    }}
                  >
                    <span style={{ color: "#64748b" }}>Submitted:</span>
                    <span style={{ fontSize: "0.75rem" }}>
                      {new Date(request.timestamp).toLocaleString()}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Modal for viewing loan details */}
      {viewingLoan && (
  <div className="modal-overlay" onClick={() => setViewingLoan(null)}>
    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
      <div className="modal-header">
        <h2>Application Details</h2>
        <button className="modal-close" onClick={() => setViewingLoan(null)}>
          ×
        </button>
      </div>

      <div className="modal-body">
        {/* Personal Information */}
        <div className="modal-section">
              <h3
                style={{
                  fontSize: "1.125rem",
                  fontWeight: "600",
                  marginBottom: "0.75rem",
                }}
              >
                Personal Information
              </h3>
             <div className="detail-grid">
                <div>
                  <p
                    style={{
                      fontSize: "0.875rem",
                      color: "#64748b",
                      marginBottom: "0.25rem",
                    }}
                  >
                    First Name
                  </p>
                  <p style={{ fontWeight: "500", margin: 0 }}>
                    {viewingLoan.personalInfo?.firstName || "N/A"}
                  </p>
                </div>
                <div>
                  <p
                    style={{
                      fontSize: "0.875rem",
                      color: "#64748b",
                      marginBottom: "0.25rem",
                    }}
                  >
                    Last Name
                  </p>
                  <p style={{ fontWeight: "500", margin: 0 }}>
                    {viewingLoan.personalInfo?.lastName || "N/A"}
                  </p>
                </div>
                <div>
                  <p
                    style={{
                      fontSize: "0.875rem",
                      color: "#64748b",
                      marginBottom: "0.25rem",
                    }}
                  >
                    Email
                  </p>
                  <p style={{ fontWeight: "500", margin: 0 }}>
                    {viewingLoan.personalInfo?.email || "N/A"}
                  </p>
                </div>
                <div>
                  <p
                    style={{
                      fontSize: "0.875rem",
                      color: "#64748b",
                      marginBottom: "0.25rem",
                    }}
                  >
                    Phone
                  </p>
                  <p style={{ fontWeight: "500", margin: 0 }}>
                    {viewingLoan.personalInfo?.phone || "N/A"}
                  </p>
                </div>
                <div>
                  <p
                    style={{
                      fontSize: "0.875rem",
                      color: "#64748b",
                      marginBottom: "0.25rem",
                    }}
                  >
                    Company
                  </p>
                  <p style={{ fontWeight: "500", margin: 0 }}>
                    {viewingLoan.personalInfo?.company || "N/A"}
                  </p>
                </div>
                <div>
                  <p
                    style={{
                      fontSize: "0.875rem",
                      color: "#64748b",
                      marginBottom: "0.25rem",
                    }}
                  >
                    PAN
                  </p>
                  <p style={{ fontWeight: "500", margin: 0 }}>
                    {viewingLoan.personalInfo?.pan || "N/A"}
                  </p>
                </div>
                <div>
                  <p
                    style={{
                      fontSize: "0.875rem",
                      color: "#64748b",
                      marginBottom: "0.25rem",
                    }}
                  >
                    GSTIN
                  </p>
                  <p style={{ fontWeight: "500", margin: 0 }}>
                    {viewingLoan.personalInfo?.gstin || "N/A"}
                  </p>
                </div>
                <div>
                  <p
                    style={{
                      fontSize: "0.875rem",
                      color: "#64748b",
                      marginBottom: "0.25rem",
                    }}
                  >
                    Date of Birth
                  </p>
                  <p style={{ fontWeight: "500", margin: 0 }}>
                    {viewingLoan.personalInfo?.dateOfBirth || "N/A"}
                  </p>
                </div>
                <div style={{ gridColumn: "1 / -1" }}>
                  <p
                    style={{
                      fontSize: "0.875rem",
                      color: "#64748b",
                      marginBottom: "0.25rem",
                    }}
                  >
                    Address
                  </p>
                  <p style={{ fontWeight: "500", margin: 0 }}>
                    {viewingLoan.personalInfo?.address || "N/A"}
                  </p>
                </div>
              </div>
            </div>

           <div className="modal-section">
          <h3>Financial Details</h3>
          <div className="detail-grid">
              
                <div>
                  <p
                    style={{
                      fontSize: "0.875rem",
                      color: "#64748b",
                      marginBottom: "0.25rem",
                    }}
                  >
                    Loan Type
                  </p>
                  <p style={{ fontWeight: "500", margin: 0 }}>
                    {viewingLoan.financialDetails?.loanType || "N/A"}
                  </p>
                </div>
                <div>
                  <p
                    style={{
                      fontSize: "0.875rem",
                      color: "#64748b",
                      marginBottom: "0.25rem",
                    }}
                  >
                    Loan Amount
                  </p>
                  <p style={{ fontWeight: "500", margin: 0 }}>
                    ₹
                    {viewingLoan.financialDetails?.loanAmount?.toLocaleString() ||
                      "0"}
                  </p>
                </div>
                <div>
                  <p
                    style={{
                      fontSize: "0.875rem",
                      color: "#64748b",
                      marginBottom: "0.25rem",
                    }}
                  >
                    Tenure
                  </p>
                  <p style={{ fontWeight: "500", margin: 0 }}>
                    {viewingLoan.financialDetails?.tenure || "N/A"} months
                  </p>
                </div>
                <div>
                  <p
                    style={{
                      fontSize: "0.875rem",
                      color: "#64748b",
                      marginBottom: "0.25rem",
                    }}
                  >
                    Monthly Income
                  </p>
                  <p style={{ fontWeight: "500", margin: 0 }}>
                    ₹
                    {viewingLoan.financialDetails?.monthlyIncome?.toLocaleString() ||
                      "0"}
                  </p>
                </div>
                <div>
                  <p
                    style={{
                      fontSize: "0.875rem",
                      color: "#64748b",
                      marginBottom: "0.25rem",
                    }}
                  >
                    Employment Type
                  </p>
                  <p style={{ fontWeight: "500", margin: 0 }}>
                    {viewingLoan.financialDetails?.employmentType || "N/A"}
                  </p>
                </div>
                <div>
                  <p
                    style={{
                      fontSize: "0.875rem",
                      color: "#64748b",
                      marginBottom: "0.25rem",
                    }}
                  >
                    Company
                  </p>
                  <p style={{ fontWeight: "500", margin: 0 }}>
                    {viewingLoan.financialDetails?.company || "N/A"}
                  </p>
                </div>
              </div>
            </div>
            </div>

            {viewingLoan.documents && (
          <div className="modal-section">
            <h3>Documents</h3>
            <div className="documents-grid">
                  {Object.entries(viewingLoan.documents).map(
                    ([docName, status]) => (
                      <div
                        key={docName}
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          padding: "0.5rem",
                          background: "#f8fafc",
                          borderRadius: "0.25rem",
                        }}
                      >
                        <span style={{ fontSize: "0.875rem" }}>{docName}</span>
                        <span style={{ fontSize: "1rem" }}>
                          {status === "yes" ? "✅" : "❌"}
                        </span>
                      </div>
                    )
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
